<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query';
import { computed, onBeforeUnmount, onMounted, shallowRef } from 'vue';
import { queryClient } from '@/app/query-client';
import { store } from '@/app/store';
import { kioskQuery, printLogoMutation } from '@/services/api/endpoints/kiosk';
import { kioskConfig } from '@/services/KioskConfig';
import {
  type ClientEvent,
  type ClientEventSnapshot,
  clientEvents,
} from '@/services/telemetry/client-events';
import { sentryService } from '@/services/telemetry/sentry';

const emit = defineEmits<{
  close: [];
}>();

const snapshot = shallowRef<ClientEventSnapshot>(clientEvents.snapshot(100));
const copyStatus = shallowRef<'idle' | 'copied' | 'failed'>('idle');
const flushStatus = shallowRef<'idle' | 'flushing' | 'flushed' | 'failed'>(
  'idle'
);
const activeTab = shallowRef<'diagnostics' | 'settings'>('diagnostics');
const printLogoToggleStatus = shallowRef<
  'idle' | 'saving' | 'saved' | 'failed'
>('idle');

let refreshTimerId: ReturnType<typeof setInterval> | null = null;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'none';
  }
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return JSON.stringify(value);
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleTimeString();
}

function formatPayload(value: unknown): string {
  if (value === null || value === undefined) {
    return '{}';
  }
  return JSON.stringify(value, null, 2);
}

function refreshSnapshot(): void {
  snapshot.value = clientEvents.snapshot(100);
}

async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error('Clipboard write failed');
  }
}

async function copyDiagnosticJson(): Promise<void> {
  copyStatus.value = 'idle';
  try {
    const diagnosticJson = JSON.stringify(clientEvents.snapshot(100), null, 2);
    await writeClipboard(diagnosticJson);
    copyStatus.value = 'copied';
    clientEvents.record(
      'diagnostics_snapshot_copied',
      { events_count: snapshot.value.events.length },
      { area: 'diagnostics' }
    );
  } catch (error) {
    copyStatus.value = 'failed';
    clientEvents.record(
      'diagnostics_snapshot_copy_failed',
      { error_message: (error as Error)?.message },
      { area: 'diagnostics', level: 'warning' }
    );
  } finally {
    refreshSnapshot();
  }
}

async function flushSentry(): Promise<void> {
  flushStatus.value = 'flushing';
  try {
    await sentryService.flush(2000);
    flushStatus.value = 'flushed';
    clientEvents.record(
      'diagnostics_sentry_flushed',
      {},
      {
        area: 'diagnostics',
      }
    );
  } catch (error) {
    flushStatus.value = 'failed';
    clientEvents.record(
      'diagnostics_sentry_flush_failed',
      { error_message: (error as Error)?.message },
      { area: 'diagnostics', level: 'warning' }
    );
  } finally {
    refreshSnapshot();
  }
}

function clearTimeline(): void {
  clientEvents.clear();
  refreshSnapshot();
}

function closePanel(): void {
  emit('close');
}

function selectTab(tab: 'diagnostics' | 'settings'): void {
  activeTab.value = tab;
}

const contextRows = computed<[string, unknown][]>(() => [
  ['trace', snapshot.value.context.frontend_trace_id],
  ['session', snapshot.value.context.session_id],
  ['device', snapshot.value.context.device_uuid],
  ['build', snapshot.value.context.build_version],
  ['kiosk', snapshot.value.context.kiosk_slug],
  ['mode', snapshot.value.context.kiosk_mode],
  ['route', snapshot.value.context.route],
  ['step', snapshot.value.context.step],
]);

const state = computed(() => snapshot.value.state as Record<string, unknown>);
const printLogoConfig = computed(() => store.kiosk?.print_logo_config ?? null);
const isPrintLogoConfigured = computed(() =>
  Boolean(printLogoConfig.value?.url)
);
const isPrintLogoEnabled = computed(
  () => printLogoConfig.value?.enabled === true
);
const printLogoStatusLabel = computed(() => {
  if (!isPrintLogoConfigured.value) {
    return 'Not configured';
  }
  if (printLogoToggleStatus.value === 'saving') {
    return 'Saving...';
  }
  if (printLogoToggleStatus.value === 'failed') {
    return 'Update failed';
  }
  return isPrintLogoEnabled.value ? 'Enabled' : 'Disabled';
});
const networkState = computed(() => asRecord(state.value.network));
const paymentState = computed(() => asRecord(state.value.payment));
const printingState = computed(() => asRecord(state.value.printing));
const sessionState = computed(() => asRecord(state.value.session));
const events = computed<ClientEvent[]>(() => snapshot.value.events);

const printLogo = useMutation(printLogoMutation());

function setPrintLogoEnabled(enabled: boolean): void {
  kioskConfig.updateKiosk((kiosk) =>
    kiosk.print_logo_config
      ? { ...kiosk, print_logo_config: { ...kiosk.print_logo_config, enabled } }
      : kiosk
  );
}

async function togglePrintLogo(): Promise<void> {
  const kioskSlug = store.kiosk?.slug;
  if (
    !isPrintLogoConfigured.value ||
    printLogoToggleStatus.value === 'saving' ||
    !kioskSlug
  ) {
    return;
  }

  const enabled = !isPrintLogoEnabled.value;
  printLogoToggleStatus.value = 'saving';
  clientEvents.record(
    'print_logo_toggle_started',
    { enabled },
    { area: 'printing' }
  );

  try {
    const response = await printLogo.mutateAsync({ kioskSlug, enabled });
    setPrintLogoEnabled(response.enabled);
    printLogoToggleStatus.value = 'saved';
    clientEvents.record(
      'print_logo_toggle_succeeded',
      { enabled: response.enabled },
      { area: 'printing' }
    );
  } catch (error) {
    const kiosk = store.kiosk;
    if (kiosk?.slug) {
      try {
        const freshKiosk = await queryClient.fetchQuery({
          ...kioskQuery(kiosk.slug),
          staleTime: 0,
        });
        kioskConfig.updateKiosk((current) => ({
          ...current,
          print_logo_config: freshKiosk.print_logo_config,
        }));
        if (freshKiosk.print_logo_config?.enabled === enabled) {
          printLogoToggleStatus.value = 'saved';
          clientEvents.record(
            'print_logo_toggle_reconciled',
            { enabled },
            { area: 'printing', level: 'warning' }
          );
          return;
        }
      } catch (_resyncError) {
        // The original mutation error is recorded below.
      }
    }
    printLogoToggleStatus.value = 'failed';
    clientEvents.record(
      'print_logo_toggle_failed',
      {
        enabled,
        error_message: (error as Error)?.message,
      },
      { area: 'printing', level: 'error' }
    );
  }
}

interface SummaryBlock {
  title: string;
  last: unknown;
  rows: [string, unknown][];
}

function areaSummary(
  title: string,
  area: string,
  counters: [string, string[]][]
): SummaryBlock {
  const areaEvents = events.value.filter((event) => event.area === area);
  return {
    title,
    last: areaEvents.at(-1)?.type ?? null,
    rows: counters.map(([label, types]) => [
      label,
      areaEvents.filter((event) => types.includes(event.type)).length,
    ]),
  };
}

const summaryBlocks = computed((): SummaryBlock[] => {
  const payment = areaSummary('Payment', 'payment', [
    ['captured', ['payment_captured']],
    ['failed', ['payment_failed', 'payment_timeout', 'payment_cancelled']],
  ]);
  const printer = areaSummary('Printer', 'printing', [
    ['copies ok', ['print_copy_succeeded']],
    ['copies failed', ['print_copy_failed']],
  ]);
  return [
    areaSummary('Scanner', 'scanner', [
      ['classified', ['scanner_input_classified']],
      ['registered', ['scanner_code_registered']],
      ['failed', ['scanner_failed']],
    ]),
    {
      ...payment,
      rows: [
        ['active', displayValue(paymentState.value?.isPaymentInProgress)],
        ...payment.rows,
      ],
    },
    {
      ...printer,
      rows: [
        ['queue', displayValue(printingState.value?.queueLength)],
        ...printer.rows,
      ],
    },
    {
      title: 'Session',
      last: sessionState.value?.sessionId,
      rows: [
        ['resetting', displayValue(sessionState.value?.isResettingSession)],
        ['gophoto', displayValue(sessionState.value?.gophotoCodeSet)],
        ['events', events.value.length],
      ],
    },
  ];
});

onMounted(() => {
  clientEvents.record('diagnostics_panel_opened', {}, { area: 'diagnostics' });
  refreshSnapshot();
  refreshTimerId = setInterval(refreshSnapshot, 1000);
});

onBeforeUnmount(() => {
  if (refreshTimerId !== null) {
    clearInterval(refreshTimerId);
    refreshTimerId = null;
  }
  clientEvents.record('diagnostics_panel_closed', {}, { area: 'diagnostics' });
});
</script>

<template>
  <div class="KioskDiagnosticsPanel" data-testid="diagnostics-panel">
    <div
      class="KioskDiagnosticsPanel__sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="operator-panel-title"
    >
      <header class="KioskDiagnosticsPanel__header">
        <div>
          <p class="KioskDiagnosticsPanel__eyebrow">Operator access</p>
          <h2 id="operator-panel-title" class="KioskDiagnosticsPanel__title">
            Operator panel
          </h2>
        </div>
        <button
          class="KioskDiagnosticsPanel__icon-button"
          type="button"
          aria-label="Close operator panel"
          @click="closePanel"
        >
          x
        </button>
      </header>

      <nav
        class="KioskDiagnosticsPanel__tabs"
        role="tablist"
        aria-label="Operator panel sections"
      >
        <button
          id="operator-diagnostics-tab"
          class="KioskDiagnosticsPanel__tab"
          :class="{ 'KioskDiagnosticsPanel__tab--active': activeTab === 'diagnostics' }"
          type="button"
          role="tab"
          data-testid="operator-panel-diagnostics-tab"
          :aria-selected="activeTab === 'diagnostics'"
          aria-controls="operator-diagnostics-panel"
          @click="selectTab('diagnostics')"
        >
          <span aria-hidden="true">01</span>
          Diagnostics
        </button>
        <button
          id="operator-settings-tab"
          class="KioskDiagnosticsPanel__tab"
          :class="{ 'KioskDiagnosticsPanel__tab--active': activeTab === 'settings' }"
          type="button"
          role="tab"
          data-testid="operator-panel-settings-tab"
          :aria-selected="activeTab === 'settings'"
          aria-controls="operator-settings-panel"
          @click="selectTab('settings')"
        >
          <span aria-hidden="true">02</span>
          Settings
        </button>
      </nav>

      <div
        v-if="activeTab === 'diagnostics'"
        id="operator-diagnostics-panel"
        role="tabpanel"
        aria-labelledby="operator-diagnostics-tab"
        data-testid="operator-panel-diagnostics"
      >
        <section class="KioskDiagnosticsPanel__section">
          <h3 class="KioskDiagnosticsPanel__section-title">Context</h3>
          <dl class="KioskDiagnosticsPanel__grid">
            <div
              v-for="[label, value] in contextRows"
              :key="label"
              class="KioskDiagnosticsPanel__field"
            >
              <dt>{{ label }}</dt>
              <dd>{{ displayValue(value) }}</dd>
            </div>
            <div class="KioskDiagnosticsPanel__field">
              <dt>network</dt>
              <dd>
                {{ displayValue(networkState?.online) }}
                <span v-if="networkState?.connection">
                  / {{ displayValue(networkState.connection) }}
                </span>
              </dd>
            </div>
          </dl>
        </section>

        <section class="KioskDiagnosticsPanel__summary">
          <article
            v-for="block in summaryBlocks"
            :key="block.title"
            class="KioskDiagnosticsPanel__summary-block"
          >
            <h3>{{ block.title }}</h3>
            <p>{{ displayValue(block.last) }}</p>
            <dl>
              <div v-for="[label, value] in block.rows" :key="label">
                <dt>{{ label }}</dt><dd>{{ value }}</dd>
              </div>
            </dl>
          </article>
        </section>

        <section class="KioskDiagnosticsPanel__actions">
          <button type="button" @click="copyDiagnosticJson">
            Copy diagnostic JSON
          </button>
          <button type="button" :disabled="flushStatus === 'flushing'" @click="flushSentry">
            Flush Sentry
          </button>
          <button type="button" @click="clearTimeline">Clear local timeline</button>
          <button type="button" @click="closePanel">Close</button>
          <span class="KioskDiagnosticsPanel__status">
            {{ copyStatus !== 'idle' ? copyStatus : flushStatus }}
          </span>
        </section>

        <section class="KioskDiagnosticsPanel__section">
          <h3 class="KioskDiagnosticsPanel__section-title">Latest Events</h3>
          <ol class="KioskDiagnosticsPanel__events">
            <li
              v-for="event in events"
              :key="event.event_id"
              class="KioskDiagnosticsPanel__event"
              :class="`KioskDiagnosticsPanel__event--${event.level}`"
            >
              <div class="KioskDiagnosticsPanel__event-row">
                <time>{{ formatTimestamp(event.timestamp) }}</time>
                <strong>{{ event.type }}</strong>
                <span>{{ event.area }} / {{ event.level }}</span>
              </div>
              <pre>{{ formatPayload(event.payload) }}</pre>
            </li>
          </ol>
        </section>
      </div>

      <div
        v-else
        id="operator-settings-panel"
        class="KioskDiagnosticsPanel__settings"
        role="tabpanel"
        aria-labelledby="operator-settings-tab"
        data-testid="operator-panel-settings"
      >
        <section class="KioskDiagnosticsPanel__settings-intro">
          <p class="KioskDiagnosticsPanel__eyebrow">Location controls</p>
          <h3>Kiosk settings</h3>
          <p>
            Changes made here are saved for this kiosk and apply to the next
            print job.
          </p>
        </section>

        <section
          class="KioskDiagnosticsPanel__setting-card"
          data-testid="diagnostics-print-logo-control"
        >
          <div class="KioskDiagnosticsPanel__logo-preview">
            <img
              v-if="isPrintLogoConfigured"
              :src="printLogoConfig?.url ?? undefined"
              alt="Configured print logo"
              data-testid="operator-panel-print-logo-preview"
            />
            <span v-else>No logo asset</span>
          </div>

          <div class="KioskDiagnosticsPanel__setting-copy">
            <p class="KioskDiagnosticsPanel__setting-kicker">Printed output</p>
            <h3>Print branding</h3>
            <p>
              Apply the configured partner logo to every photo printed from
              this kiosk.
            </p>
            <dl>
              <div>
                <dt>Applies to</dt>
                <dd>Printed photos only</dd>
              </div>
              <div>
                <dt>Placement</dt>
                <dd>{{ printLogoConfig?.position ?? 'Configured by admin' }}</dd>
              </div>
            </dl>
          </div>

          <div class="KioskDiagnosticsPanel__branding-control">
            <strong data-testid="diagnostics-print-logo-status">
              {{ printLogoStatusLabel }}
            </strong>
            <button
              class="KioskDiagnosticsPanel__switch"
              :class="{ 'KioskDiagnosticsPanel__switch--enabled': isPrintLogoEnabled }"
              type="button"
              role="switch"
              data-testid="diagnostics-print-logo-toggle"
              aria-label="Apply partner logo to printed photos"
              :aria-checked="isPrintLogoEnabled"
              :disabled="!isPrintLogoConfigured || printLogoToggleStatus === 'saving'"
              @click="togglePrintLogo"
            >
              <span class="KioskDiagnosticsPanel__switch-track" aria-hidden="true">
                <span class="KioskDiagnosticsPanel__switch-thumb" />
              </span>
              <span>{{ isPrintLogoEnabled ? 'On' : 'Off' }}</span>
            </button>
          </div>
        </section>

        <p class="KioskDiagnosticsPanel__settings-note">
          Digital downloads and the customer-facing preview are unchanged.
          Logo asset, size and placement are managed centrally.
        </p>
      </div>
    </div>
    <button
      class="KioskDiagnosticsPanel__backdrop"
      type="button"
      aria-label="Close operator panel"
      @click="closePanel"
    />
  </div>
</template>

<style scoped lang="scss">
  .KioskDiagnosticsPanel {
    --operator-accent: #5ee2ad;
    --operator-border: rgba(148, 163, 184, 0.24);
    --operator-muted: #94a3b8;
    --operator-panel: #11171d;
    --operator-surface: #172029;
    position: fixed;
    inset: 0;
    z-index: 90;
    color: #f6f8fb;
    font-family: "Avenir Next", "Helvetica Neue", sans-serif;
    font-size: 0.86rem;
    text-shadow: none;
  }

  .KioskDiagnosticsPanel__backdrop {
    position: fixed;
    inset: 0;
    z-index: 1;
    border: 0;
    background: rgba(2, 6, 23, 0.62);
  }

  .KioskDiagnosticsPanel__sheet {
    position: fixed;
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
    z-index: 2;
    max-height: min(78vh, 52rem);
    overflow: auto;
    border: 1px solid rgba(148, 163, 184, 0.38);
    border-radius: 0.8rem;
    background: var(--operator-panel);
    box-shadow: 0 1rem 4rem rgba(0, 0, 0, 0.45);
  }

  .KioskDiagnosticsPanel__header {
    position: sticky;
    top: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--operator-border);
    background: rgba(17, 23, 29, 0.97);
    backdrop-filter: blur(1rem);
  }

  .KioskDiagnosticsPanel__eyebrow,
  .KioskDiagnosticsPanel__title,
  .KioskDiagnosticsPanel__section-title,
  .KioskDiagnosticsPanel__summary-block h3,
  .KioskDiagnosticsPanel__summary-block p,
  .KioskDiagnosticsPanel__field dt,
  .KioskDiagnosticsPanel__field dd {
    margin: 0;
  }

  .KioskDiagnosticsPanel__eyebrow {
    margin: 0;
    color: var(--operator-accent);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.12rem;
    text-transform: uppercase;
  }

  .KioskDiagnosticsPanel__title {
    margin-top: 0.14rem;
    font-size: 1.22rem;
    line-height: 1.1;
  }

  .KioskDiagnosticsPanel__icon-button,
  .KioskDiagnosticsPanel__actions button {
    border: 1px solid rgba(148, 163, 184, 0.42);
    border-radius: 0.35rem;
    background: #1f2937;
    color: #f8fafc;
    font: inherit;
    line-height: 1;
    cursor: pointer;
  }

  .KioskDiagnosticsPanel__icon-button {
    width: 2.1rem;
    height: 2.1rem;
    padding: 0;
    font-size: 1.15rem;
  }

  .KioskDiagnosticsPanel__tabs {
    position: sticky;
    top: 4.1rem;
    z-index: 3;
    display: flex;
    gap: 0.25rem;
    padding: 0.5rem 1rem 0;
    border-bottom: 1px solid var(--operator-border);
    background: rgba(17, 23, 29, 0.97);
    backdrop-filter: blur(1rem);
  }

  .KioskDiagnosticsPanel__tab {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-height: 2.8rem;
    padding: 0 1rem;
    border: 0;
    border-bottom: 0.18rem solid transparent;
    background: transparent;
    color: var(--operator-muted);
    font: inherit;
    font-weight: 650;
    cursor: pointer;
  }

  .KioskDiagnosticsPanel__tab span {
    color: #5f7181;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.64rem;
  }

  .KioskDiagnosticsPanel__tab--active {
    border-bottom-color: var(--operator-accent);
    color: #f8fafc;
  }

  .KioskDiagnosticsPanel__tab--active span {
    color: var(--operator-accent);
  }

  .KioskDiagnosticsPanel__tab:focus-visible,
  .KioskDiagnosticsPanel__icon-button:focus-visible,
  .KioskDiagnosticsPanel__actions button:focus-visible,
  .KioskDiagnosticsPanel__switch:focus-visible {
    outline: 0.15rem solid #93c5fd;
    outline-offset: 0.12rem;
  }

  .KioskDiagnosticsPanel__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.6rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--operator-border);
    background: rgba(23, 32, 41, 0.56);
  }

  .KioskDiagnosticsPanel__actions button {
    min-height: 2.05rem;
    padding: 0 0.72rem;
  }

  .KioskDiagnosticsPanel__actions button:disabled {
    opacity: 0.55;
    cursor: wait;
  }

  .KioskDiagnosticsPanel__section,
  .KioskDiagnosticsPanel__summary {
    padding: 0.95rem 1rem;
    border-bottom: 1px solid var(--operator-border);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }

  .KioskDiagnosticsPanel__section-title {
    margin-bottom: 0.66rem;
    color: #e2e8f0;
    font-size: 0.82rem;
    text-transform: uppercase;
  }

  .KioskDiagnosticsPanel__grid,
  .KioskDiagnosticsPanel__summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .KioskDiagnosticsPanel__field,
  .KioskDiagnosticsPanel__summary-block {
    min-width: 0;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 0.35rem;
    background: rgba(15, 23, 42, 0.58);
    padding: 0.58rem;
  }

  .KioskDiagnosticsPanel__field dt,
  .KioskDiagnosticsPanel__summary-block dt {
    color: #94a3b8;
    font-size: 0.65rem;
    text-transform: uppercase;
  }

  .KioskDiagnosticsPanel__field dd,
  .KioskDiagnosticsPanel__summary-block dd,
  .KioskDiagnosticsPanel__summary-block p {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .KioskDiagnosticsPanel__summary-block h3 {
    margin-bottom: 0.35rem;
    color: #a7f3d0;
    font-size: 0.82rem;
  }

  .KioskDiagnosticsPanel__summary-block dl {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.4rem;
    margin: 0.5rem 0 0;
  }

  .KioskDiagnosticsPanel__summary-block dl div {
    min-width: 0;
  }

  .KioskDiagnosticsPanel__status {
    color: var(--operator-accent);
  }

  .KioskDiagnosticsPanel__events {
    display: grid;
    gap: 0.45rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .KioskDiagnosticsPanel__event {
    border-left: 0.22rem solid #64748b;
    border-radius: 0.35rem;
    background: rgba(15, 23, 42, 0.74);
    padding: 0.55rem 0.65rem;
  }

  .KioskDiagnosticsPanel__event--warning {
    border-left-color: #f59e0b;
  }

  .KioskDiagnosticsPanel__event--error {
    border-left-color: #ef4444;
  }

  .KioskDiagnosticsPanel__event-row {
    display: grid;
    grid-template-columns: 5.2rem minmax(0, 1fr) max-content;
    gap: 0.6rem;
    align-items: baseline;
  }

  .KioskDiagnosticsPanel__event-row strong,
  .KioskDiagnosticsPanel__event-row span,
  .KioskDiagnosticsPanel__event-row time {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .KioskDiagnosticsPanel__event-row time,
  .KioskDiagnosticsPanel__event-row span {
    color: #94a3b8;
  }

  .KioskDiagnosticsPanel__event pre {
    margin: 0.45rem 0 0;
    overflow: auto;
    color: #dbeafe;
    font: inherit;
    line-height: 1.35;
    white-space: pre-wrap;
  }

  .KioskDiagnosticsPanel__settings {
    min-height: 24rem;
    padding: clamp(1.25rem, 3vw, 2.5rem);
    background:
      radial-gradient(circle at 100% 0, rgba(94, 226, 173, 0.08), transparent 32%),
      var(--operator-panel);
  }

  .KioskDiagnosticsPanel__settings-intro {
    max-width: 42rem;
    margin-bottom: 1.5rem;
  }

  .KioskDiagnosticsPanel__settings-intro h3 {
    margin: 0.3rem 0 0.35rem;
    font-size: clamp(1.35rem, 2.4vw, 2rem);
    letter-spacing: -0.025em;
  }

  .KioskDiagnosticsPanel__settings-intro > p:last-child,
  .KioskDiagnosticsPanel__settings-note,
  .KioskDiagnosticsPanel__setting-copy > p {
    margin: 0;
    color: #a9b6c2;
    line-height: 1.55;
  }

  .KioskDiagnosticsPanel__setting-card {
    display: grid;
    grid-template-columns: minmax(9rem, 0.7fr) minmax(18rem, 2fr) minmax(9rem, auto);
    align-items: center;
    gap: clamp(1rem, 2.5vw, 2rem);
    padding: clamp(1rem, 2.5vw, 1.6rem);
    border: 1px solid rgba(148, 163, 184, 0.3);
    border-radius: 0.75rem;
    background: linear-gradient(135deg, rgba(28, 39, 49, 0.98), rgba(20, 28, 35, 0.98));
    box-shadow: 0 1rem 2.4rem rgba(0, 0, 0, 0.16);
  }

  .KioskDiagnosticsPanel__logo-preview {
    display: grid;
    place-items: center;
    min-height: 7.5rem;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.18);
    border-radius: 0.55rem;
    background-color: #fff;
    background-image:
      linear-gradient(45deg, #edf1f4 25%, transparent 25%),
      linear-gradient(-45deg, #edf1f4 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #edf1f4 75%),
      linear-gradient(-45deg, transparent 75%, #edf1f4 75%);
    background-position: 0 0, 0 0.5rem, 0.5rem -0.5rem, -0.5rem 0;
    background-size: 1rem 1rem;
    color: #475569;
    font-size: 0.74rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  .KioskDiagnosticsPanel__logo-preview img {
    display: block;
    width: min(100%, 14rem);
    max-height: 6rem;
    object-fit: contain;
  }

  .KioskDiagnosticsPanel__setting-kicker {
    color: var(--operator-accent) !important;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1rem;
    text-transform: uppercase;
  }

  .KioskDiagnosticsPanel__setting-copy h3 {
    margin: 0.22rem 0 0.35rem;
    font-size: 1.15rem;
  }

  .KioskDiagnosticsPanel__setting-copy dl {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin: 1rem 0 0;
  }

  .KioskDiagnosticsPanel__setting-copy dl div {
    display: grid;
    gap: 0.12rem;
  }

  .KioskDiagnosticsPanel__setting-copy dt {
    color: #718294;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.06rem;
    text-transform: uppercase;
  }

  .KioskDiagnosticsPanel__setting-copy dd {
    margin: 0;
    color: #dce5ec;
    text-transform: capitalize;
  }

  .KioskDiagnosticsPanel__branding-control {
    display: grid;
    justify-items: end;
    gap: 0.45rem;
    color: #e2e8f0;
  }

  .KioskDiagnosticsPanel__branding-control strong {
    color: #b8c5cf;
    font-size: 0.75rem;
  }

  .KioskDiagnosticsPanel__switch {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    min-width: 6.8rem;
    min-height: 2.75rem;
    padding: 0 0.8rem;
    border: 1px solid #526171;
    border-radius: 999px;
    background: #222d37;
    color: #f8fafc;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    transition: border-color 160ms ease, background-color 160ms ease;
  }

  .KioskDiagnosticsPanel__switch-track {
    position: relative;
    width: 2.4rem;
    height: 1.35rem;
    border-radius: 999px;
    background: #526171;
    transition: background-color 160ms ease;
  }

  .KioskDiagnosticsPanel__switch-thumb {
    position: absolute;
    top: 0.18rem;
    left: 0.18rem;
    width: 0.99rem;
    height: 0.99rem;
    border-radius: 50%;
    background: #f8fafc;
    box-shadow: 0 0.12rem 0.35rem rgba(0, 0, 0, 0.32);
    transition: transform 160ms ease;
  }

  .KioskDiagnosticsPanel__switch--enabled {
    border-color: #43c994;
    background: #163d31;
  }

  .KioskDiagnosticsPanel__switch--enabled .KioskDiagnosticsPanel__switch-track {
    background: #35b982;
  }

  .KioskDiagnosticsPanel__switch--enabled .KioskDiagnosticsPanel__switch-thumb {
    transform: translateX(1.05rem);
  }

  .KioskDiagnosticsPanel__switch:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }

  .KioskDiagnosticsPanel__settings-note {
    max-width: 50rem;
    margin-top: 1rem;
    padding-left: 0.85rem;
    border-left: 0.15rem solid #4d5f6f;
    font-size: 0.78rem;
  }

  @media (max-width: 1180px) {
    .KioskDiagnosticsPanel__grid,
    .KioskDiagnosticsPanel__summary {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .KioskDiagnosticsPanel__event-row {
      grid-template-columns: 1fr;
      gap: 0.2rem;
    }

    .KioskDiagnosticsPanel__setting-card {
      grid-template-columns: minmax(8rem, 0.65fr) minmax(0, 2fr);
    }

    .KioskDiagnosticsPanel__branding-control {
      grid-column: 1 / -1;
      grid-template-columns: 1fr auto;
      align-items: center;
      justify-items: start;
    }
  }

  @media (max-width: 620px) {
    .KioskDiagnosticsPanel__sheet {
      inset: 0.5rem;
      max-height: calc(100vh - 1rem);
    }

    .KioskDiagnosticsPanel__tabs {
      top: 4.05rem;
    }

    .KioskDiagnosticsPanel__tab {
      flex: 1;
      justify-content: center;
      padding: 0 0.5rem;
    }

    .KioskDiagnosticsPanel__settings {
      padding: 1rem;
    }

    .KioskDiagnosticsPanel__setting-card {
      grid-template-columns: 1fr;
    }

    .KioskDiagnosticsPanel__logo-preview {
      min-height: 6.5rem;
    }

    .KioskDiagnosticsPanel__branding-control {
      grid-column: auto;
    }
  }
</style>
